import { z } from 'zod';

// --- Section keys (§5.1.1) ---

export const medicalSectionKeyValues = [
  'my_exams',
  'employees_medical_table',
  'plan_schedule',
  'problem_employees',
  'create_medical_referral',
] as const;

export type MedicalSectionKey = (typeof medicalSectionKeyValues)[number];

export const medicalSectionKeyLabels: Record<MedicalSectionKey, string> = {
  my_exams: 'Мои медосмотры',
  employees_medical_table: 'Таблица медосмотров',
  plan_schedule: 'План-график',
  problem_employees: 'Проблемные сотрудники',
  create_medical_referral: 'Направления',
};

export const medicalSectionSchema = z.object({
  key: z.string(),
});

export type MedicalSection = z.infer<typeof medicalSectionSchema>;

export const medicalSectionsResponseSchema = z.object({
  sections: z.array(medicalSectionSchema),
});

// --- Shared refs ---

export const positionRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const orgUnitRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

// --- Medical check row (used in list + employee card) ---

export const medicalCheckRowSchema = z.object({
  id: z.string(),
  check_type: z.string(),
  status: z.string(),
  decision: z.string().nullable().optional(),
  admission_status: z.string(),
  checked_at: z.string().nullable().optional(),
  next_check_date: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  conclusion_summary: z.string().nullable().optional(),
  restrictions: z.string().nullable().optional(),
  is_current: z.boolean(),
});

export type MedicalCheckRow = z.infer<typeof medicalCheckRowSchema>;

// --- Employees list (§5.2.1) ---

export const medicalCheckEmployeeRowSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  tab_number: z.string().nullable().optional(),
  is_active: z.boolean(),
  position: positionRefSchema.nullable().optional(),
  org_unit: orgUnitRefSchema,
  current_check: medicalCheckRowSchema.nullable().optional(),
  recent_checks: z.array(medicalCheckRowSchema),
  current_admission_status: z.string(),
});

export type MedicalCheckEmployeeRow = z.infer<typeof medicalCheckEmployeeRowSchema>;

export const medicalCheckEmployeesResponseSchema = z.object({
  employees: z.array(medicalCheckEmployeeRowSchema),
  next_page_token: z.string().nullable().optional(),
});

// --- Employee detail card (§5.3.1) ---

export const upcomingActionSchema = z.object({
  title: z.string(),
  due_date: z.string().nullable().optional(),
  status: z.string(),
});

export type UpcomingAction = z.infer<typeof upcomingActionSchema>;

export const medicalCheckEmployeeDetailSchema = z.object({
  id: z.string(),
  full_name: z.string(),
  tab_number: z.string().nullable().optional(),
  is_active: z.boolean(),
  position: positionRefSchema.nullable().optional(),
  org_unit: orgUnitRefSchema,
  admission_indicator: z.string(),
});

export type MedicalCheckEmployeeDetail = z.infer<typeof medicalCheckEmployeeDetailSchema>;

export const getEmployeeCardResponseSchema = z.object({
  employee: medicalCheckEmployeeDetailSchema,
  history: z.array(medicalCheckRowSchema),
  upcoming_actions: z.array(upcomingActionSchema),
});

// --- Exam detail (§5.4.1) ---

export const medicalExamDetailSchema = z.object({
  id: z.string(),
  employee_id: z.string(),
  employee_full_name: z.string(),
  employee_tab_number: z.string().nullable().optional(),
  org_unit: orgUnitRefSchema,
  position: positionRefSchema.nullable().optional(),
  check_type: z.string(),
  status: z.string(),
  decision: z.string().nullable().optional(),
  admission_status: z.string(),
  reason: z.string().nullable().optional(),
  checked_at: z.string().nullable().optional(),
  next_check_date: z.string().nullable().optional(),
  conclusion_summary: z.string().nullable().optional(),
  restrictions: z.string().nullable().optional(),
});

export type MedicalExamDetail = z.infer<typeof medicalExamDetailSchema>;

export const getExamDetailResponseSchema = z.object({
  exam: medicalExamDetailSchema,
});

// --- Enums / labels ---

export const checkTypeValues = ['preliminary', 'periodic', 'extraordinary', 'pre_shift', 'post_shift'] as const;

export type CheckType = (typeof checkTypeValues)[number];

export const checkTypeLabels: Record<CheckType, string> = {
  preliminary: 'Предварительный',
  periodic: 'Периодический',
  extraordinary: 'Внеочередной',
  pre_shift: 'Предсменный',
  post_shift: 'Послесменный',
};

export const checkStatusValues = [
  'not_assigned',
  'assigned',
  'in_progress',
  'passed',
  'failed',
  'overdue',
  'not_passed',
] as const;

export type CheckStatus = (typeof checkStatusValues)[number];

export const checkStatusLabels: Record<CheckStatus, string> = {
  not_assigned: 'Не назначен',
  assigned: 'Назначен',
  in_progress: 'В процессе',
  passed: 'Пройден',
  failed: 'Не пройден',
  overdue: 'Просрочен',
  not_passed: 'Не пройден',
};

export const admissionStatusValues = [
  'allowed',
  'not_allowed',
  'allowed_with_restrictions',
  'temporary_not_allowed',
  'permanent_not_allowed',
  'not_assigned',
] as const;

export type AdmissionStatus = (typeof admissionStatusValues)[number];

export const admissionStatusLabels: Record<AdmissionStatus, string> = {
  allowed: 'Допущен',
  not_allowed: 'Не допущен',
  allowed_with_restrictions: 'С ограничениями',
  temporary_not_allowed: 'Врем. недопуск',
  permanent_not_allowed: 'Пост. недопуск',
  not_assigned: 'Не определён',
};

export const decisionValues = [
  'fit',
  'fit_with_restrictions',
  'temporary_unfit',
  'permanent_unfit',
  'allowed',
  'allowed_with_restrictions',
  'temporary_not_allowed',
  'permanent_not_allowed',
] as const;

export type Decision = (typeof decisionValues)[number];

export const decisionLabels: Record<Decision, string> = {
  fit: 'Годен',
  fit_with_restrictions: 'Годен с ограничениями',
  temporary_unfit: 'Временно не годен',
  permanent_unfit: 'Не годен',
  allowed: 'Допуск',
  allowed_with_restrictions: 'Допуск с ограничениями',
  temporary_not_allowed: 'Временный недопуск',
  permanent_not_allowed: 'Постоянный недопуск',
};

// --- Widgets (§US-2.10) ---

export const medicalAttentionItemSchema = z.object({
  employee_id: z.string(),
  employee_name: z.string(),
  exam_label: z.string(),
  detail: z.string(),
});

export type MedicalAttentionItem = z.infer<typeof medicalAttentionItemSchema>;

export const summaryWidgetSchema = z.object({
  contingent_total: z.number(),
  planned_count: z.number(),
  passed_count: z.number(),
  overdue_count: z.number(),
  not_allowed_count: z.number(),
  attention_items: z.array(medicalAttentionItemSchema),
});

export type SummaryWidget = z.infer<typeof summaryWidgetSchema>;

export const summaryWidgetResponseSchema = z.object({
  widget: summaryWidgetSchema,
});

export const riskDefaultFiltersSchema = z.object({
  statuses: z.array(z.string()),
  admission_statuses: z.array(z.string()),
});

export const riskWidgetSchema = z.object({
  total_at_risk: z.number(),
  failed_count: z.number(),
  overdue_count: z.number(),
  not_allowed_count: z.number(),
  delta_to_previous_period: z.number().nullable().optional(),
  severity: z.string(),
  default_filters: riskDefaultFiltersSchema,
});

export type RiskWidget = z.infer<typeof riskWidgetSchema>;

export const riskWidgetResponseSchema = z.object({
  widget: riskWidgetSchema,
});

// --- Exam events (§5.4.2) ---

export const medicalExamEventSchema = z.object({
  id: z.string(),
  event_type: z.string(),
  old_value: z.record(z.string(), z.unknown()).nullable().optional(),
  new_value: z.record(z.string(), z.unknown()).nullable().optional(),
  changed_by_user_id: z.string(),
  changed_at: z.string(),
  reason: z.string().nullable().optional(),
});

export type MedicalExamEvent = z.infer<typeof medicalExamEventSchema>;

export const listExamEventsResponseSchema = z.object({
  events: z.array(medicalExamEventSchema),
});

// --- Exam comments (§5.4.3) ---

export const medicalExamCommentSchema = z.object({
  id: z.string(),
  author_id: z.string(),
  author_name: z.string().nullable().optional(),
  body: z.string(),
  is_edited: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type MedicalExamComment = z.infer<typeof medicalExamCommentSchema>;

export const listExamCommentsResponseSchema = z.object({
  comments: z.array(medicalExamCommentSchema),
});

// --- Exam documents (§5.5.1) ---

export const medicalExamDocumentSchema = z.object({
  id: z.string(),
  doc_type: z.string(),
  file_name: z.string(),
  mime_type: z.string(),
  version: z.number(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  is_deleted: z.boolean(),
});

export type MedicalExamDocument = z.infer<typeof medicalExamDocumentSchema>;

export const listExamDocumentsResponseSchema = z.object({
  documents: z.array(medicalExamDocumentSchema),
});

export const eventTypeLabels: Record<string, string> = {
  status_changed: 'Изменение статуса',
  decision_changed: 'Изменение решения',
  comment_added: 'Комментарий',
  document_uploaded: 'Загрузка документа',
};

export const docTypeLabels: Record<string, string> = {
  referral: 'Направление',
  conclusion: 'Заключение',
  attachment: 'Вложение',
};

// --- Referral actions (§5.6) ---

export const referralValidationItemSchema = z.object({
  item_id: z.string(),
  employee_id: z.string(),
  validation_status: z.string(),
  validation_details: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const referralValidationSchema = z.object({
  id: z.string(),
  status: z.string(),
  blocking_errors_count: z.number(),
  warnings_count: z.number(),
});

export const validateReferralResponseSchema = z.object({
  referral: referralValidationSchema,
  items_sample: z.array(referralValidationItemSchema),
});

export type ValidateReferralResult = z.infer<typeof validateReferralResponseSchema>;

export const issueReferralBodySchema = z.object({
  id: z.string(),
  status: z.string(),
  issued_at: z.string(),
});

export const issueReferralResponseSchema = z.object({
  referral: issueReferralBodySchema,
});

export type IssueReferralResult = z.infer<typeof issueReferralResponseSchema>;

export const exportReferralItemSchema = z.object({
  item_id: z.string(),
  employee_id: z.string(),
  full_name: z.string(),
  tab_number: z.string().nullable().optional(),
  validation_status: z.string(),
  validation_details: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const exportReferralBodySchema = z.object({
  id: z.string(),
  status: z.string(),
  template_code: z.string(),
  issued_at: z.string().nullable().optional(),
  created_by: z.string(),
  generated_at: z.string(),
  items: z.array(exportReferralItemSchema),
});

export const exportReferralResponseSchema = z.object({
  referral: exportReferralBodySchema,
});

export type ExportReferralResult = z.infer<typeof exportReferralResponseSchema>;
